import * as THREE from 'three';
import type { SurfaceBlock } from '@vrlearning/lesson-schema';
import { foliage, type WorldBuilder } from '../world.js';
import { effectMaterial } from './effects.js';

/** A surface block's material: its textures asked for now, in the colour of their average until they arrive. */
function surfaceMaterial(world: WorldBuilder, block: SurfaceBlock): THREE.MeshStandardMaterial {
  const material = world.surface({
    asset: 'platform' in block.texture ? block.texture.platform : block.texture.file,
    maps: block.maps,
    tint: block.tint,
    roughness: block.roughness,
    vertexColors: block.vertexColours,
    ...(block.overlay
      ? {
          overlay: {
            asset: block.overlay.platform,
            tile: block.overlay.tile,
            tint: block.overlay.tint,
          },
        }
      : {}),
  });
  if (block.polygonOffset) {
    material.polygonOffset = true;
    material.polygonOffsetFactor = 2;
    material.polygonOffsetUnits = 2;
  }
  return material;
}

/** A block's material: one of the headset's own shaders when it names an effect (its clock on the world's), else its textures. */
function blockMaterial(world: WorldBuilder, block: SurfaceBlock): THREE.Material {
  if (block.effect) {
    const made = effectMaterial(world, block.effect);
    world.onFrame((_dt, elapsed) => made.update(elapsed));
    return made.material;
  }
  const material = surfaceMaterial(world, block);
  // Cut-out leaves (alpha to coverage, both sides; foliage() turns those on for an alpha-tested material).
  if (block.cutout) {
    material.alphaTest = 0.5;
    material.transparent = false;
  }
  return material;
}

/**
 * The materials of surface blocks, made at build time: their textures are then asked for with the
 * rest of the world's files, which the lesson waits for (WorldLoader.ready) -- a texture first asked
 * for when a GLB arrives would come too late for the first look and go in at the next fade.
 */
export function surfaceMaterials(
  world: WorldBuilder,
  blocks: readonly SurfaceBlock[],
): Map<string, THREE.Material> {
  return new Map(blocks.map((block) => [block.id, blockMaterial(world, block)]));
}

const swayed = new WeakSet<THREE.Material>();

/**
 * The surface block (W2; from WorldBuilder.surface): a loaded GLB's meshes named `<mesh>…` dressed in
 * a texture set -- the platform's (`{platform}`) or the package's own (`{file}`, its manifest entry's
 * asset is the file name) -- with the grass overlay mixed in by the mesh's `_COVER` attribute.
 * `materials` are the blocks' own, made by surfaceMaterials; without them they are made here.
 */
export function dressSurfaces(
  world: WorldBuilder,
  model: THREE.Object3D,
  blocks: readonly SurfaceBlock[],
  materials: Map<string, THREE.Material> = new Map(),
): void {
  // Meshes that sway: per block, the lowest and highest point of the meshes it dresses here.
  const swaying = new Map<string, { low: number; high: number }>();
  model.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    const block = blocks.find((b) => node.name.startsWith(b.mesh));
    if (!block) return;
    const geometry = node.geometry as THREE.BufferGeometry;
    // GLTFLoader names a glTF attribute `_COVER` as `_cover`; the overlay's shader reads `cover`.
    const cover = geometry.getAttribute('_cover');
    if (cover) geometry.setAttribute('cover', cover);
    let material = materials.get(block.id);
    if (!material) {
      material = blockMaterial(world, block);
      materials.set(block.id, material);
    }
    (node.material as THREE.Material).dispose();
    node.material = material;
    if (!block.effect && (block.sway || block.cutout)) {
      geometry.computeBoundingBox();
      const box = geometry.boundingBox!;
      const known = swaying.get(block.id);
      swaying.set(block.id, {
        low: Math.min(known?.low ?? Infinity, box.min.y),
        high: Math.max(known?.high ?? -Infinity, box.max.y),
      });
    }
  });
  // Sway grows from the dressed meshes' lowest point to their top; a material is made to sway once.
  for (const [id, { low, high }] of swaying) {
    const material = materials.get(id)!;
    if (swayed.has(material)) continue;
    swayed.add(material);
    const block = blocks.find((b) => b.id === id)!;
    foliage(
      material,
      block.sway ?? 0,
      { wind: world.wind, base: low, height: Math.max(high - low, 0.01) },
      { phase: 'position' },
    );
  }
}
